import * as React from 'react'
import { connect } from 'react-redux'

import { DefaultContext, interpret, Machine } from 'xstate'

import {
  LifeInsuranceAnswerOption,
  LifeInsuranceQuestion,
  LifeInsuranceQuestionType,
  Product,
  TrackingKey,
} from 'even-ts-static'

import {
  applicationFieldChanged,
  applicationFieldRegistered,
  applicationFieldShowValidationStatus,
} from 'common/actions/application'
import { ContextTitle } from 'embed/components/Application/ContextTitle'
import { StepTitle } from 'embed/components/Application/StepTitle'
import { ApplicationField } from 'embed/components/ApplicationField/container'
import {
  ApplicationFields,
  ButtonListField,
  ButtonListFieldDirectionOption,
  DropdownItem,
  Field,
  FieldKey,
  FreeformMultipleText,
  LifeInsuranceApplicationStep,
  MappedLifeInsuranceQuestions,
  MultiButtonListField,
  MultiProductDispatch,
  TextField,
} from 'embed/constants'
import Tracker from 'common/services/Tracker'

import {
  FinalStateNodeNames,
  QuestionMachineConfig,
  QuestionMachineContext,
  QuestionMachineEventType,
  questionMachineOptions,
  QuestionStateMachineInterpreter,
} from 'embed/constants/life-insurance'
import { lifeInsuranceApplicationFieldsSelector } from 'embed/selectors/application'
import Parent from 'common/services/Parent'
import { getQuestionUuidFromQuestionMachineState, getResponsePathWithSteps } from 'embed/utils/life-insurance'

import { MultiProductState } from 'embed/reducers'

import { ParentProps } from './router-questions-container'

import { StepbystepWrapper as Wrapper } from '../Wrapper'

const styles = require('./styles.scss')

interface StoreProps {
  fields: ApplicationFields | undefined
}

interface DispatchProps {
  dispatchApplicationFieldChanged: (fieldKey: string, value: string) => void
  dispatchApplicationFieldRegistered: (fieldKey: string) => void
  dispatchApplicationFieldShowValidationStatus: (fieldKey: FieldKey, show: boolean) => void
}

type Props = Omit<ParentProps, 'groups'> &
  StoreProps &
  DispatchProps & {
    questions: MappedLifeInsuranceQuestions
    questionMachineConfig: QuestionMachineConfig
  }

const getOptions = (options: LifeInsuranceAnswerOption[]): DropdownItem<string>[] | undefined =>
  options?.map((option) => ({ label: option.answerLabel, value: option.answerValue }))

export const getField = (question: LifeInsuranceQuestion): Field | null => {
  const { questionType, uuid, allowNoSelection, answerOptions = [] } = question
  switch (questionType) {
    case LifeInsuranceQuestionType.Binary:
    case LifeInsuranceQuestionType.SelectOne:
      const selectOptions = getOptions(answerOptions)
      return new ButtonListField(uuid, {
        direction: (selectOptions ? selectOptions.length < 3 : true)
          ? ButtonListFieldDirectionOption.Row
          : ButtonListFieldDirectionOption.Column,
        hideValidationStatus: false,
        required: true,
        selectOptions,
      })
    case LifeInsuranceQuestionType.FreeformOne:
      return new TextField(uuid, {
        hideValidationStatus: false,
        required: true,
      })
    case LifeInsuranceQuestionType.FreeformMultiple:
      return new FreeformMultipleText(uuid, {
        hideValidationStatus: false,
        required: true,
      })
    case LifeInsuranceQuestionType.SelectMultiple:
      return new MultiButtonListField(uuid, {
        hideValidationStatus: false,
        required: true,
        selectOptions: getOptions(answerOptions),
        direction: ButtonListFieldDirectionOption.Column,
        allowNone: allowNoSelection,
      })
    default:
      return null
  }
}

function usePrevious(value?: string) {
  const ref = React.useRef('')
  React.useEffect(() => {
    if (value) {
      ref.current = value
    }
  })
  return ref?.current
}

const delay = 200

interface UseQuestionMachineReturnType {
  currentFieldKey: string | undefined
  context: QuestionMachineContext | undefined
  send: QuestionStateMachineInterpreter | undefined
  resetMachine: (config: QuestionMachineConfig) => void
}
// Custom hook because we need some more functionality than what's provided by the official hook in @xstate/react
export const useQuestionMachine = (questionMachineConfig?: QuestionMachineConfig): UseQuestionMachineReturnType => {
  const [currentState, setCurrentState] = React.useState<string | undefined>(undefined)
  const [context, setContext] = React.useState<QuestionMachineContext | undefined>(undefined)
  const [interpretedMachine, setInterpretedMachine] = React.useState<DefaultContext | undefined>(undefined)

  const createAndSetMachine = (config: QuestionMachineConfig) => {
    setInterpretedMachine(
      interpret(Machine(config, questionMachineOptions)).onTransition((state) => {
        setCurrentState(state.toStrings()[0])
        setContext(state.context)
      }),
    )
  }

  React.useEffect(() => {
    if (questionMachineConfig !== undefined) {
      createAndSetMachine(questionMachineConfig)
    }
  }, [questionMachineConfig?.initial])

  React.useEffect(() => {
    if (interpretedMachine !== undefined) {
      interpretedMachine.start()
    }
  }, [interpretedMachine])

  const send = interpretedMachine?.send

  return {
    currentFieldKey: currentState && getQuestionUuidFromQuestionMachineState(currentState),
    context,
    send,
    resetMachine: createAndSetMachine,
  }
}

export const UnconnectedRouterQuestions: React.SFC<Props> = (props) => {
  const {
    dispatchApplicationFieldChanged,
    dispatchApplicationFieldRegistered,
    dispatchApplicationFieldShowValidationStatus,
    fields,
    onBack,
    onContinue,
    questionMachineConfig,
    questions,
    setCurrentRouterStep,
    setFollowupStep,
    shouldReplayResponses,
  } = props
  // When this component first mounts, we use getResponsePathWithSteps to replay the state machine with the fields
  // that are already in the store. This may happen if the user has gone past the router questions and is coming back
  // to them. In that case, we'll initialize the state machine with the penultimate state (the last one is the
  // 'complete' state)
  const [initialConfig] = React.useState(() => {
    if (shouldReplayResponses) {
      const responsePathWithSteps = getResponsePathWithSteps(questionMachineConfig, fields)
      const initialState = responsePathWithSteps.responsePath.slice(-2)[0]?.currentState
      const steps = initialState ? responsePathWithSteps.stepMap[initialState] : undefined
      return { initialState, currentStep: steps?.currentStep ?? 0, followupStep: steps?.followupStep ?? 0 }
    }
  })
  const { currentFieldKey, context, send, resetMachine } = useQuestionMachine(
    initialConfig?.initialState === undefined
      ? questionMachineConfig
      : {
          ...questionMachineConfig,
          initial: initialConfig.initialState,
          context: {
            ...questionMachineConfig.context,
            currentStep: initialConfig.currentStep,
            followupStep: initialConfig.followupStep,
          },
        },
  )
  const currentField = currentFieldKey ? fields?.[currentFieldKey] : undefined

  React.useEffect(() => {
    if (currentFieldKey !== undefined && currentField === undefined) {
      dispatchApplicationFieldRegistered(currentFieldKey)
    }
  }, [currentFieldKey])

  const currentStep = context?.currentStep
  const followupStep = context?.followupStep
  React.useEffect(() => {
    if (currentStep !== undefined) {
      setCurrentRouterStep(currentStep)
    }
  }, [currentStep])
  React.useEffect(() => {
    if (followupStep !== undefined) {
      setFollowupStep(followupStep)
    }
  }, [followupStep])

  React.useEffect(() => {
    if (currentFieldKey === FinalStateNodeNames.Complete) {
      onContinue()
    }
  }, [currentFieldKey])

  React.useEffect(() => {
    if (currentFieldKey !== FinalStateNodeNames.Complete) {
      Tracker.trackEvent({
        action: TrackingKey.ApplicationStepSelected,
        data: {
          applicationStep: currentFieldKey,
          product: Product.LifeInsurance,
        },
        timestamp: new Date(),
      })
    }
  }, [currentFieldKey])

  const onQuestionMachineBack = () => {
    // Since there is no linear path back through the machine states, we need to replay the machine with what's in the
    // store and initialize a new machine with the second most recent state as the initial value
    const responsePathWithSteps = getResponsePathWithSteps(questionMachineConfig, fields)
    const { responsePath: fullResponsePath, stepMap } = responsePathWithSteps
    const currentFieldKeyIdx = fullResponsePath.reduce((final: number | undefined, response, idx) => {
      if (getQuestionUuidFromQuestionMachineState(response.currentState) === currentFieldKey) {
        return idx
      } else {
        return final
      }
    }, undefined)
    const newInitialResponse =
      currentFieldKeyIdx !== undefined ? fullResponsePath[currentFieldKeyIdx - 1] : fullResponsePath.slice(-1)[0]
    if (
      fullResponsePath.length === 0 ||
      newInitialResponse === undefined ||
      getQuestionUuidFromQuestionMachineState(newInitialResponse.currentState) === currentFieldKey
    ) {
      onBack()
    } else {
      const { currentState } = newInitialResponse
      const steps = stepMap[currentState]
      resetMachine({
        ...questionMachineConfig,
        initial: newInitialResponse.currentState,
        context: { ...questionMachineConfig.context, currentStep: steps.currentStep, followupStep: steps.followupStep },
      })
    }
  }
  const onQuestionMachineContinue = () => {
    if (currentField?.isValid) {
      send?.({ type: QuestionMachineEventType.ANSWER, value: currentField.value })

      // Clear the currently focused element
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
      }

      // Scroll to the top of the embed after each question is answered
      Parent.scrollToFrame()
    } else {
      if (currentFieldKey !== undefined) {
        dispatchApplicationFieldShowValidationStatus(currentFieldKey, true)
      }
    }
  }

  const previousFieldKey = usePrevious(currentFieldKey)
  React.useEffect(() => {
    if (
      question &&
      (question?.questionType === LifeInsuranceQuestionType.Binary ||
        question?.questionType === LifeInsuranceQuestionType.SelectOne) &&
      previousFieldKey === currentFieldKey &&
      currentField?.isValid
    ) {
      setTimeout(onQuestionMachineContinue, delay)
    }
  }, [currentField?.isValid, currentField?.value])

  // If 'none of these' is selected, auto advance
  React.useEffect(() => {
    if (
      question &&
      question?.questionType === LifeInsuranceQuestionType.SelectMultiple &&
      previousFieldKey === currentFieldKey &&
      currentField?.isValid &&
      Array.isArray(currentField?.value) &&
      currentField?.value.length === 0
    ) {
      setTimeout(onQuestionMachineContinue, delay)
    }
  }, [currentField?.isValid, currentField?.value])

  const question = currentFieldKey !== undefined && questions[currentFieldKey]

  const onChange = (value: string) => {
    if (currentFieldKey !== undefined) {
      dispatchApplicationFieldChanged(currentFieldKey, value)
    }
  }

  const field = question && getField(question)
  return question ? (
    <Wrapper
      {...props}
      stepByStepApplicationStep={LifeInsuranceApplicationStep.RouterQuestions}
      stepByStepApplicationStepIsValid={currentFieldKey !== undefined && fields?.[currentFieldKey]?.isValid}
      onBack={onQuestionMachineBack}
      onContinue={onQuestionMachineContinue}
      key={question.uuid}
    >
      {question.contextTitle !== undefined && <ContextTitle>{question.contextTitle}</ContextTitle>}
      <StepTitle>{question.prompt}</StepTitle>
      {field && (
        <ApplicationField styles={styles.formRow} onChange={onChange} product={Product.LifeInsurance} field={field} />
      )}
      {question.helperText !== undefined && <p className={styles.helperText}>{question.helperText}</p>}
    </Wrapper>
  ) : (
    <Wrapper
      {...props}
      stepByStepApplicationStepIsValid={currentFieldKey !== undefined && fields?.[currentFieldKey]?.isValid}
      onBack={onQuestionMachineBack}
      onContinue={onQuestionMachineContinue}
    >
      {null}
    </Wrapper>
  )
}

const mapStateToProps = (state: MultiProductState): StoreProps => ({
  fields: lifeInsuranceApplicationFieldsSelector(state),
})

const mapDispatchToProps = (dispatch: MultiProductDispatch): DispatchProps => ({
  dispatchApplicationFieldChanged: (fieldKey: string, value) =>
    dispatch(applicationFieldChanged(Product.LifeInsurance, fieldKey, value, true)),
  dispatchApplicationFieldRegistered: (fieldKey: string) =>
    dispatch(applicationFieldRegistered(Product.LifeInsurance, fieldKey)),
  dispatchApplicationFieldShowValidationStatus: (fieldKey: FieldKey, show: boolean) =>
    dispatch(applicationFieldShowValidationStatus(Product.LifeInsurance, fieldKey, show)),
})

export const RouterQuestions = connect(mapStateToProps, mapDispatchToProps)(UnconnectedRouterQuestions)
